import React, { useState } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { usePurgeDemo } from 'hooks/api'
import { DemoListItem } from 'services/demosApi'

import { Stack, Typography } from '@mui/material'

import { Alert, Button, Modal, TextArea } from '@repo/ui'
import { CompanyBlock } from 'components'

interface PurgeDemoModalProps {
  demo: DemoListItem | null
  open: boolean
  onClose: () => void
}

/**
 * VRG-313 AC5 / VRG-311 AC7 — first-class containment action, not a rarely-used
 * escape hatch (AC4: there's no automatic expiry, so this list + this modal are
 * the whole mitigation for accumulation). Deletes the company's entire graph and
 * storage; irreversible, so it goes through `ConfirmSettingModal`-style consequence
 * copy plus a REQUIRED typed reason (the runbook depends on it when a brand
 * objects months later — see purge-demo-company.use-case.ts).
 */
export const PurgeDemoModal = ({ demo, open, onClose }: PurgeDemoModalProps) => {
  const { open: notify } = useSnackbar()
  const [reason, setReason] = useState('')

  const { mutate: purge, isPending } = usePurgeDemo()

  const handleClose = () => {
    setReason('')
    onClose()
  }

  const handleConfirm = () => {
    if (!demo || !reason.trim()) return
    purge(
      { companyId: demo.companyId, reason: reason.trim() },
      {
        onSuccess: () => {
          handleClose()
        },
        onError: (error) => {
          notify({ type: 'error', title: getErrorMessage(error) })
        },
      }
    )
  }

  return (
    <Modal
      title="Purge demo"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            destructive
            onClick={handleConfirm}
            disabled={isPending || !reason.trim()}
            loading={isPending}
          >
            Purge demo
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {demo && (
          <CompanyBlock company={{ name: demo.companyName, logo: demo.logo }} />
        )}

        <Alert
          severity="warning"
          description="This permanently deletes the demo company, its certificate, products and every stored asset. The public demo page stops resolving. This cannot be undone."
        />

        <Stack spacing={2}>
          <Typography variant="body3">Reason</Typography>
          <TextArea
            placeholder="Why is this demo being purged?"
            value={reason}
            onValueChange={(v: string) => setReason(v)}
            minRows={3}
          />
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Required. Recorded in the purge audit so a brand complaint months from now
            has an answer.
          </Typography>
        </Stack>
      </Stack>
    </Modal>
  )
}
